// Chart.js plugins are registered globally in src/chartjs-init.ts
// and configured here via options.plugins.

import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

// eslint-disable-next-line import/no-duplicates
import { Locale } from "date-fns";
// eslint-disable-next-line import/no-duplicates
import { enGB, ru } from "date-fns/locale";

import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Interaction,
  Chart,
  Scale,
  ScriptableScaleContext,
  TooltipItem,
} from "chart.js";
import "chartjs-adapter-date-fns";

import { RangeType, useAsu } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import { ExternalTooltip, Interpolate } from "../../../../../../../../components/common/chart-plugins";
import { useAlarmPaletteResolver } from "../../../../../../../../hooks/use-alarm-color";
import { formatTrendTooltipDate } from "../../../../../../../../utils";

import { getLineColor } from "../../utils/line-colors";
import { IChartData } from "../../interfaces";
import { themeProps } from "../../../../../../../../components/theme";
import { ChartZoomOverview } from "../chart-zoom-overview";
import { CHART_SELECTION_EVENTS, IChartZoomControlProps, applyZoomBounds, useChartZoom } from "../chart-zoom";
import { StyledWrapper, StyledChartContainer, StyledLegendContainer } from "./styled";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";
import {
  createExportCanvas,
  exportChart,
  IChartExportControlProps,
  TChartExportCanvasFactory,
  getChartExportFileName,
} from "../chart-export";
import {
  buildAmplitudePhaseTooltipPairs,
  findAmplitudePhaseTooltipPair,
  formatAmplitudePhaseTooltipContent,
} from "../../utils/amplitude-phase-tooltip";
import { buildChartLegendLabels } from "./chart-legend-labels";
import { ITrendChartTimeBounds, resolveTrendChartTimeDomain } from "./time-domain";
import { getTrendYAxisDisplay, getTrendYAxisPosition, shouldDrawTrendYAxisGrid } from "./trend-y-axis-policy";
import { isPhaseSeries, withUnwrappedPhase } from "../../utils/amplitude-phase";
import { getSeriesKey } from "../../utils/series-key";

const locales: { [key: string]: Locale } = { en: enGB, ru };
// @ts-ignore
Interaction.modes.index = Interpolate;

export interface IMultilineChart extends IChartZoomControlProps, IChartExportControlProps {
  colorBySeries?: Map<string, string>;
  data: IChartData[];
  allAmplitudePhaseData?: IChartData[] | null;
  chartTitle: string;
  legendContainerId: string;
  captureSelectionOutsideCanvas?: boolean;
  hideLegend?: boolean;
  onCursorDateChange?: (date: string | null) => void;
  timeBounds?: ITrendChartTimeBounds | null;
  externalChartRef?: MutableRefObject<any>;
  syncChartRef?: MutableRefObject<any>;
  datasetVisibility?: Record<string, boolean>;
  onDatasetVisibilityChange?: (seriesKey: string, visible: boolean) => void;
  exportCanvasFactoryRef?: MutableRefObject<TChartExportCanvasFactory | null>;
  suppressInternalLegendContainer?: boolean;
}

interface ITrendTooltipPointRaw {
  sourceDate?: string;
  x: string;
  y: number;
  rotationFrequencyRpm?: number | null;
}

interface IYAxis {
  type: "linear";
  display: boolean | "auto";
  position: "left" | "right";
  id: string;
  grace: string;
  title: {
    display: boolean;
    text: string;
    font: {
      size: number;
      family: string;
    };
    color: string;
  };
  ticks: {
    font: {
      size: number;
      family: string;
    };
    callback?: (value: number) => number | null;
  };
  grid: {
    drawOnChartArea: (context: ScriptableScaleContext) => boolean;
  };
  offset?: boolean;
  min?: number;
  max?: number;
}

const getFontFamily = () =>
  typeof themeProps?.options?.typography === "function"
    ? "Roboto"
    : themeProps?.options?.typography?.fontFamily ?? "Roboto";

export const MultilineChart: FC<IMultilineChart> = props => {
  const {
    colorBySeries,
    data,
    allAmplitudePhaseData,
    chartTitle,
    legendContainerId,
    registerReset,
    registerExport,
    onZoomStateChange,
    captureSelectionOutsideCanvas = false,
    hideLegend = false,
    onCursorDateChange,
    timeBounds,
    externalChartRef,
    syncChartRef,
    datasetVisibility,
    onDatasetVisibilityChange,
    exportCanvasFactoryRef,
    suppressInternalLegendContainer,
  } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ChartJS<"line"> | null>(null);
  const resolveAlarmColor = useAlarmPaletteResolver();
  const { profile } = useUser<IUser>();

  const {
    startTimestamp,
    endTimestamp,
    userSelectedRangeType,
    userSelectedRecorderModePeriod,
    recorderModeInterval,
    recorderWindowStart,
    recorderWindowEnd,
  } = useAsu();
  const isSecondsRecorderMode =
    userSelectedRangeType === RangeType.RECORDER_MODE && userSelectedRecorderModePeriod === "seconds";
  const useSecondsTimeScale =
    isSecondsRecorderMode ||
    (userSelectedRangeType === RangeType.RECORDER_MODE &&
      userSelectedRecorderModePeriod === "minutes" &&
      recorderModeInterval <= 3);
  const deferChartUntilRecorderBounds =
    useSecondsTimeScale && userSelectedRangeType === RangeType.RECORDER_MODE && !recorderWindowStart;
  const chartFallbackStart =
    userSelectedRangeType === RangeType.RECORDER_MODE && recorderWindowStart ? recorderWindowStart : startTimestamp;
  const chartFallbackEnd =
    userSelectedRangeType === RangeType.RECORDER_MODE && recorderWindowEnd ? recorderWindowEnd : endTimestamp;

  const { locale, formatMessage } = useIntl();
  const { zoomState, applySelectionZoom, clearZoomState, resetZoom, isZoomed, overview, panZoom, overviewPlugin } =
    useChartZoom(
      chartRef,
      {
        registerReset,
        onZoomStateChange,
      },
      data,
    );

  const getLocale = useCallback((): Locale => {
    return locales[locale.toLowerCase()];
  }, [locale]);

  const applyDatasetVisibility = useCallback(() => {
    const chart = chartRef.current;
    if (!chart || !datasetVisibility) return;

    data.forEach((series, i) => {
      const key = getSeriesKey(series.idPoint, series.parameterName);

      if (key && key in datasetVisibility) {
        chart.setDatasetVisibility(i, datasetVisibility[key]);
      }
    });

    chart.update();
  }, [data, datasetVisibility]);

  const displayData = useMemo(() => data.map(withUnwrappedPhase), [data]);

  const amplitudePhaseTooltipPairs = useMemo(
    () => buildAmplitudePhaseTooltipPairs((allAmplitudePhaseData ?? data).map(withUnwrappedPhase)),
    [allAmplitudePhaseData, data],
  );

  const formatAmplitudePhaseTooltip = useCallback(
    (dataPoint: TooltipItem<"line">) => {
      const series = data[dataPoint.datasetIndex];

      if (series?.isAmplitudePhase !== true) {
        return null;
      }

      const raw = dataPoint.raw as ITrendTooltipPointRaw | undefined;
      const pair = findAmplitudePhaseTooltipPair(amplitudePhaseTooltipPairs, series, raw?.sourceDate);

      return pair
        ? formatAmplitudePhaseTooltipContent(pair, {
            dataFormat: profile.interfaceSettings.dataFormat,
            rpmUnitLabel: formatMessage({ id: "pages.scheme.analytics.rotationFrequencyRpmUnit" }),
          })
        : null;
    },
    [amplitudePhaseTooltipPairs, data, formatMessage, profile.interfaceSettings.dataFormat],
  );

  const formatAmplitudePhaseTooltipDataPoints = useCallback(
    (dataPoints: TooltipItem<"line">[]) => {
      const dataPoint = dataPoints.find(({ datasetIndex }) => data[datasetIndex]?.isAmplitudePhase === true);

      return dataPoint ? formatAmplitudePhaseTooltip(dataPoint) : null;
    },
    [data, formatAmplitudePhaseTooltip],
  );

  const formatTrendTooltipContent = useCallback(
    (dataPoints: TooltipItem<"line">[]) => {
      const amplitudeContent = formatAmplitudePhaseTooltipDataPoints(dataPoints);
      if (amplitudeContent) {
        return amplitudeContent;
      }

      const dataPoint = dataPoints[0];
      const raw = dataPoint.raw as ITrendTooltipPointRaw | undefined;
      const sourceDate = raw?.sourceDate;

      if (!sourceDate) {
        return null;
      }

      const series = data[dataPoint.datasetIndex];
      const parameterName = (series?.parameterLabel ?? series?.parameterName)?.trim();
      const pointName = String(dataPoint.dataset.label ?? "").trim();
      const title = parameterName ? `${parameterName}: ${pointName}` : pointName;

      return {
        titleLines: [title, formatTrendTooltipDate(sourceDate)],
      };
    },
    [data, formatAmplitudePhaseTooltipDataPoints],
  );

  const isCombinedAmplitudePhaseChart = allAmplitudePhaseData === data;

  const chartData = useMemo<ChartData<"line", any[]>>(
    () => ({
      datasets: displayData.map((series, i) => {
        const seriesKey = getSeriesKey(series.idPoint, series.parameterName) ?? undefined;
        const baseColor = (seriesKey ? colorBySeries?.get(seriesKey) : undefined) ?? series.color ?? getLineColor(i);

        return {
          type: "line",
          label: series.name,
          borderColor: isCombinedAmplitudePhaseChart && isPhaseSeries(series) ? `${baseColor}80` : baseColor,
          data: series.items.map(item => ({
            sourceDate: item.date,
            x: item.date.replace("Z", ""),
            y: item.value,
            rotationFrequencyRpm: item.rotationFrequencyRpm ?? null,
          })),
          pointHoverBorderWidth: 8,
          yAxisID: series.units,
          parameterName: series.parameterName,
          idPoint: series.idPoint,
        };
      }),
    }),
    [displayData, colorBySeries, allAmplitudePhaseData],
  );

  const handleChartClick = useCallback(
    (event: unknown, elements: Array<{ datasetIndex: number; index: number }>, chart: Chart<"line">) => {
      if (!onCursorDateChange) {
        return;
      }

      const activeElements = elements.length
        ? elements
        : chart.getElementsAtEventForMode(
            event as Parameters<Chart<"line">["getElementsAtEventForMode"]>[0],
            "nearest",
            { intersect: false, axis: "x" },
            true,
          );
      const activeElement = activeElements[0];
      const item = activeElement ? data[activeElement.datasetIndex]?.items[activeElement.index] : null;

      onCursorDateChange(item?.date ?? null);
    },
    [data, onCursorDateChange],
  );

  const { minDate, maxDate } = useMemo(
    () =>
      resolveTrendChartTimeDomain({
        data,
        fallbackStart: chartFallbackStart,
        fallbackEnd: chartFallbackEnd,
        timeBounds,
      }),
    [chartFallbackEnd, chartFallbackStart, data, timeBounds],
  );

  const getTimeUnit = useCallback(() => {
    if (useSecondsTimeScale) return "second";

    const diffMs = maxDate.getTime() - minDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 365 * 2) return "year";
    if (diffDays > 60) return "month";
    if (diffDays > 14) return "week";
    if (diffDays > 2) return "day";
    if (diffDays > 1) return "hour";
    return "minute";
  }, [maxDate, minDate, useSecondsTimeScale]);

  const uniqueUnits = useMemo(() => Array.from(new Set(data.map(({ units }) => units))), [data]);
  const showMultitrendScales = profile.interfaceSettings.showMultitrendScales ?? false;

  const yAxisStats = useMemo(
    () =>
      displayData.reduce<Record<string, { min: number; max: number }>>((acc, { units, items }) => {
        const values = items
          .map(({ value }) => Number(value))
          .filter((value): value is number => Number.isFinite(value));

        if (values.length === 0) {
          return acc;
        }

        const currentMin = Math.min(...values);
        const currentMax = Math.max(...values);
        const prev = acc[units];

        if (!prev) {
          acc[units] = { min: currentMin, max: currentMax };
          return acc;
        }

        acc[units] = {
          min: Math.min(prev.min, currentMin),
          max: Math.max(prev.max, currentMax),
        };

        return acc;
      }, {}),
    [displayData],
  );

  const yAxisDisplay = getTrendYAxisDisplay({
    uniqueUnitCount: uniqueUnits.length,
    allSeriesAreAmplitudePhase: data.every(item => item.isAmplitudePhase === true),
    showMultitrendScales,
  });

  const uniqueParameterNames = useMemo(
    () => Array.from(new Set(data.map(({ parameterName }) => parameterName))),
    [data],
  );
  const showAlarmLevels = uniqueParameterNames?.length === 1;

  const yAxes = useMemo(
    () =>
      uniqueUnits.reduce<Record<string, IYAxis>>((acc, units, index) => {
        const axisStats = yAxisStats[units];
        const axisRange = axisStats ? axisStats.max - axisStats.min : 0;
        const axisPadding = axisStats
          ? axisRange > 0
            ? axisRange * 0.05
            : Math.max(1, Math.abs(axisStats.min) * 0.05)
          : 0;
        const axisMin = axisStats ? axisStats.min - axisPadding : undefined;

        const axisConfig: IYAxis = {
          type: "linear",
          display: yAxisDisplay,
          position: getTrendYAxisPosition(index, showMultitrendScales),
          id: units,
          grace: "10%",
          ...(axisMin !== undefined ? { min: axisMin } : {}),
          title: {
            display: yAxisDisplay !== false,
            text: units,
            font: {
              size: 12,
              family: getFontFamily(),
            },
            color: "rgba(0, 0, 0, 0.87)",
          },
          ticks: {
            font: {
              size: 11,
              family: getFontFamily(),
            },
            callback: (value: number) => {
              const numericValue = Number(value);
              if (Number.isNaN(numericValue)) {
                return null;
              }

              if (
                ["\u0418\u0422\u0421", "TCI"].includes(units.toUpperCase()) &&
                (numericValue > 100 || numericValue < 0)
              ) {
                return null;
              }

              return Number(numericValue.toFixed(1));
            },
          },
          grid: {
            drawOnChartArea: shouldDrawTrendYAxisGrid,
          },
        };

        acc[units] = applyZoomBounds(axisConfig, zoomState?.y?.[units]);
        return acc;
      }, {}),
    [uniqueUnits, yAxisDisplay, yAxisStats, zoomState, showMultitrendScales],
  );

  const options = useMemo<ChartOptions<"line"> & { plugins: any }>(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      events: CHART_SELECTION_EVENTS,
      onClick: handleChartClick,
      scales: {
        x: applyZoomBounds(
          {
            min: minDate.getTime(),
            max: maxDate.getTime(),
            adapters: {
              date: {
                locale: getLocale(),
              },
            },
            afterBuildTicks: (scale: Scale) => {
              if (!useSecondsTimeScale) return;

              const stepMs = 10_000;
              const firstTick = Math.ceil(scale.min / stepMs) * stepMs;
              scale.ticks = Array.from(
                { length: Math.max(0, Math.floor((scale.max - firstTick) / stepMs) + 1) },
                (_, index) => ({ value: firstTick + index * stepMs }),
              );
            },
            ticks: {
              maxTicksLimit: 20,
              font: {
                size: 12,
                family: getFontFamily(),
              },
            },
            type: "time" as const,
            time: {
              unit: getTimeUnit() as "second" | "minute" | "hour" | "day" | "week" | "month" | "year",
              ...(useSecondsTimeScale ? { stepSize: 10 } : {}),
              displayFormats: {
                year: "yyyy",
                month: "MM.yyyy",
                week: "dd.MM.yy",
                day: "dd.MM.yy",
                hour: "dd.MM.yy/HH:mm",
                minute: "HH:mm",
                second: "HH:mm:ss",
              },
              tooltipFormat: "dd.MM.yy/HH:mm:ss",
            },
          },
          zoomState?.x,
        ),
        ...yAxes,
      },
      elements: {
        point: {
          borderWidth: 0.5,
        },
      },
      interaction: {
        intersect: false,
        mode: "nearest" as const,
        axis: "xy",
      },
      plugins: {
        title: {
          display: false,
          text: chartTitle,
          font: {
            size: 10,
            family: getFontFamily(),
          },
        },
        alarmLevel: showAlarmLevels
          ? {
              level2: data[0].alarmLevel2,
              level3: data[0].alarmLevel3,
              level4: data[0].alarmLevel4,
              level2Color: resolveAlarmColor(30),
              level3Color: resolveAlarmColor(60),
              level4Color: resolveAlarmColor(90),
              yAxisID: uniqueUnits[0],
            }
          : false,
        htmlLegend: hideLegend
          ? undefined
          : {
              containerId: legendContainerId,
              fontFamily: getFontFamily(),
              syncChartRefs: syncChartRef ? [syncChartRef] : [],
              onDatasetVisibilityChange,
            },
        legend: {
          display: false,
          labels: {
            boxWidth: 10,
            padding: 10,
            font: {
              size: 10,
              family: getFontFamily(),
            },
            generateLabels: (chart: Chart<"line">) => {
              return buildChartLegendLabels(chart, allAmplitudePhaseData ?? data, colorBySeries);
            },
          },
        },
        tooltip: {
          enabled: false,
          intersect: false,
          mode: "nearest" as const,
          external: context => ExternalTooltip({ ...context, chart: context.chart as Chart<"line"> }),
          unitsX: "",
          unitsY: data[0]?.units,
          yValueFormatter: (value: number, dataPoint: TooltipItem<"line">) => {
            return formatMeasurementNumber(
              value,
              data[dataPoint.datasetIndex]?.unitCode ?? null,
              profile.interfaceSettings.dataFormat,
              { fallbackDigits: 2 },
            );
          },
          contentFormatter: formatTrendTooltipContent,
        },
        crosshair: {
          enabled: true,
          line: {
            color: getLineColor(0),
            width: 0.5,
          },
          sync: {
            enabled: false,
          },
          zoom: {
            enabled: false,
          },
        },
        selectionZoom: {
          enabled: true,
          captureOutsideCanvas: captureSelectionOutsideCanvas,
          xScaleId: "x",
          yScaleIds: uniqueUnits,
          minDragPx: 8,
          onZoom: applySelectionZoom,
          onReset: clearZoomState,
        },
      },
    }),
    [
      minDate,
      maxDate,
      getLocale,
      yAxes,
      showAlarmLevels,
      data,
      uniqueUnits,
      chartTitle,
      resolveAlarmColor,
      getTimeUnit,
      useSecondsTimeScale,
      zoomState,
      legendContainerId,
      captureSelectionOutsideCanvas,
      hideLegend,
      syncChartRef,
      allAmplitudePhaseData,
      colorBySeries,
      onDatasetVisibilityChange,
      handleChartClick,
      applySelectionZoom,
      clearZoomState,
      formatMessage,
      formatTrendTooltipContent,
      profile.interfaceSettings.dataFormat,
    ],
  );

  const renderChart = useCallback(() => {
    if (!canvasRef.current) return;

    const chart = new ChartJS<"line">(canvasRef.current, {
      type: "line",
      data: chartData,
      options,
      plugins: [overviewPlugin],
    });
    chartRef.current = chart;
    if (externalChartRef) {
      externalChartRef.current = chart;
    }
    applyDatasetVisibility();
  }, [chartData, options, externalChartRef, applyDatasetVisibility, overviewPlugin]);

  const destroyChart = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
      if (externalChartRef) {
        externalChartRef.current = null;
      }
    }
  }, [externalChartRef]);

  const handleResize = useCallback(() => {
    requestAnimationFrame(() => {
      chartRef.current?.resize();
    });
  }, []);

  useEffect(() => {
    if (deferChartUntilRecorderBounds) {
      destroyChart();
      return undefined;
    }

    renderChart();
    return destroyChart;
  }, [deferChartUntilRecorderBounds, renderChart, destroyChart]);

  useEffect(() => {
    const observer = new ResizeObserver(handleResize);

    if (chartContainerRef.current) {
      observer.observe(chartContainerRef.current);
    }

    handleResize();

    return () => {
      observer.disconnect();
    };
  }, [handleResize, data]);

  useEffect(() => {
    resetZoom();
  }, [data, resetZoom]);

  useEffect(() => {
    if (!exportCanvasFactoryRef || !canvasRef.current) {
      return;
    }

    const factory = () => {
      const width = 1024;
      const height = width * (canvasRef.current!.height / canvasRef.current!.width);

      return createExportCanvas(chartData, options, data, width, height, colorBySeries);
    };

    exportCanvasFactoryRef.current = factory;

    return () => {
      if (exportCanvasFactoryRef.current === factory) {
        exportCanvasFactoryRef.current = null;
      }
    };
  }, [exportCanvasFactoryRef, chartData, options, data, colorBySeries]);

  useEffect(() => {
    registerExport?.(format => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const width = 1024;
      const height = width * (canvas.height / canvas.width);
      const exportItem = createExportCanvas(chartData, options, data, width, height, colorBySeries);

      const fileName = getChartExportFileName(format);
      const exportFormat = format === "png" ? "image/png" : "image/jpeg";

      exportChart(exportItem, exportFormat, fileName);
    });

    return () => {
      registerExport?.(null);
    };
  }, [registerExport, chartData, options, data, colorBySeries]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMiddleClick = (event: MouseEvent) => {
      if (event.button === 1 && isZoomed) {
        event.preventDefault();
        event.stopImmediatePropagation();
        resetZoom();
      }
    };

    canvas.addEventListener("mousedown", handleMiddleClick, true);
    canvas.addEventListener("auxclick", handleMiddleClick, true);
    return () => {
      canvas.removeEventListener("mousedown", handleMiddleClick, true);
      canvas.removeEventListener("auxclick", handleMiddleClick, true);
    };
  }, [isZoomed, resetZoom]);

  return (
    <StyledWrapper>
      <StyledChartContainer ref={chartContainerRef}>
        <ChartZoomOverview overview={overview} zoomState={zoomState} onPan={panZoom} />
        <canvas
          ref={canvasRef}
          role="img"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
          }}
        />
      </StyledChartContainer>
      {!hideLegend && !suppressInternalLegendContainer ? (
        <StyledLegendContainer id={legendContainerId} sx={{ overflow: "auto" }} />
      ) : null}
    </StyledWrapper>
  );
};
